import React, { useState, useCallback, useRef, useEffect } from 'react'
import ReactCrop from 'react-image-crop'
import 'react-image-crop/dist/ReactCrop.css'
import { FormattedMessage, useIntl } from 'react-intl'
import common from '@/utils/common'

export const ImageUploadCrop = ({
  aspect,
  setThumbnail,
  setPicture,
  unit,
  width,
  fileTypesAllowed,
  fileSizeAllowed,
}) => {
  const [upImg, setUpImg] = useState()
  const imgRef = useRef(null)
  const [crop, setCrop] = useState({
    unit: unit || 'px', // %
    width: width || 200,
    aspect: aspect ? aspect : 1 / 1,
  })
  const [completedCrop, setCompletedCrop] = useState(null)
  const intl = useIntl()
  const defaultFileSizeAllowed = 12500000
  const defaultFileTypesAllowed = [
    'jpg',
    'png',
    'jpeg',
    'svg',
    'gif',
    'bitmap',
    'webp',
  ]
  const showErrorMessage = (msgVal, type) => {
    const validationMsg = intl.formatMessage({
      id: type === 1 ? 'FILE_SIZE_ERROR' : 'FILE_TYPE_ERROR',
      defaultMessage: msgVal,
    })
    common.errorToast(validationMsg)
  }
  const onSelectFile = (e) => {
    let fileSizeMessage = ''
    let fileTypeMsg = ''
    fileSizeAllowed = fileSizeAllowed ?? defaultFileSizeAllowed
    fileTypesAllowed = fileTypesAllowed ?? defaultFileTypesAllowed
    if (e.target.files && e.target.files.length > 0) {
      if (e.target.files[0].size > fileSizeAllowed) {
        fileSizeMessage = `Oops! Your file is too large. The max file size is ${fileSizeAllowed}. Please upload a smaller file.`
        showErrorMessage(fileSizeMessage, 1)
      }
      if (!fileTypesAllowed.some((t) => e.target.files[0].type.includes(t))) {
        fileTypeMsg =
          'Oops! The selected file type is not allowed. You can upload either a jpg, jpeg, png, svg, gif, bitmap or webp.'
        showErrorMessage(fileTypeMsg, 2)
      }
      if (fileSizeMessage === '' && fileTypeMsg === '') {
        const reader = new FileReader()
        reader.addEventListener('load', () => setUpImg(reader.result))
        reader.readAsDataURL(e.target.files[0])
      } else {
        e.target.value = null
      }
    }
  }

  const onLoad = useCallback((img) => {
    imgRef.current = img
  }, [])

  useEffect(() => {
    if (!completedCrop || !imgRef.current) {
      return
    }
    const image = imgRef.current
    const crop = completedCrop
    const scaleX = image.naturalWidth / image.width
    const scaleY = image.naturalHeight / image.height
    if (setPicture) {
      const canvasPicture = document.createElement('CANVAS')
      const ctxPicture = canvasPicture.getContext('2d')
      const pixelRatioPicture = 3 //window.devicePixelRatio
      canvasPicture.width = crop.width * pixelRatioPicture
      canvasPicture.height = crop.height * pixelRatioPicture
      ctxPicture.setTransform(pixelRatioPicture, 0, 0, pixelRatioPicture, 0, 0)
      ctxPicture.imageSmoothingQuality = 'high'
      ctxPicture.drawImage(
        image,
        crop.x * scaleX,
        crop.y * scaleY,
        crop.width * scaleX,
        crop.height * scaleY,
        0,
        0,
        crop.width,
        crop.height
      )
      canvasPicture.toBlob(
        (blob) => {
          const reader = new FileReader()
          const file = blob
          reader.onloadend = () => {
            setPicture(blob)
          }
          reader.readAsDataURL(file)
        },
        'image/png',
        1
      )
    }
    if (setThumbnail) {
      const canvasThumbnail = document.createElement('CANVAS')
      const ctxThumbnail = canvasThumbnail.getContext('2d')
      const pixelRatioThumbnail = 1 //window.devicePixelRatio
      canvasThumbnail.width = crop.width * pixelRatioThumbnail
      canvasThumbnail.height = crop.height * pixelRatioThumbnail

      ctxThumbnail.setTransform(
        pixelRatioThumbnail,
        0,
        0,
        pixelRatioThumbnail,
        0,
        0
      )
      ctxThumbnail.imageSmoothingQuality = 'high'

      ctxThumbnail.drawImage(
        image,
        crop.x * scaleX,
        crop.y * scaleY,
        crop.width * scaleX,
        crop.height * scaleY,
        0,
        0,
        crop.width,
        crop.height
      )
      canvasThumbnail.toBlob(
        (blob) => {
          try {
            const reader = new FileReader()
            const file = blob
            reader.onloadend = () => {
              setThumbnail(blob)
            }
            reader.readAsDataURL(file)
          } catch (err) {}
        },
        'image/png',
        1
      )
    }
  }, [completedCrop])

  return (
    <div className="App">
      <div>
        <input type="file" accept="image/*" onChange={onSelectFile} />
      </div>
      <div style={{ paddingTop: '20px' }}>
        <FormattedMessage
          id="FILE_UPLOAD_WARNING_MSG"
          defaultMessage={
            'File sizes are limited to 10mb or less. File formats allowed are jpg, jpeg, png, svg, gif, bitmap and webp.'
          }
        />
      </div>
      <ReactCrop
        src={upImg}
        onImageLoaded={onLoad}
        crop={crop}
        onChange={(c) => setCrop(c)}
        onComplete={(c) => setCompletedCrop(c)}
      />
    </div>
  )
}
